import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType.ts";
import {
	ConfiguratorInput,
	ConfiguratorInput_InputValue_Scalar,
} from "src/api/generated/io/aavo/applications/db/erp/types/configuratorInput";
import React, { useMemo, useRef } from "react";
import { AavoMosaic } from "src/components/common/mosaic/AavoMosaic.tsx";
import { ConfiguratorTabComponent } from "src/components/views/erp/configurator/configuratorForm/components/tab/ConfiguratorTabComponent.tsx";
import { groupConfiguratorComponentsToTabsAndSections } from "src/components/views/erp/configurator/configuratorForm/components/section/sectionGrouping.ts";
import {
	ConfiguratorSectionComponent,
	ConfiguratorSectionComponentProps,
} from "src/components/views/erp/configurator/configuratorForm/components/section/ConfiguratorSectionComponent.tsx";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialogContext.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { TransformedConfigurationComponent } from "src/api/generated/erp/configurator/componentTransformation/model/transformedConfigurationComponent.ts";
import { PartConfiguratorDocumentContextProvider } from "src/components/views/erp/configurator/configuratorForm/componentDocument/PartConfiguratorDocumentContextProvider.tsx";
import {
	ConfiguratorComponent,
	ConfiguratorComponentApi,
} from "src/components/views/erp/configurator/configuratorForm/components/ConfiguratorComponent.tsx";
import { ConfigurationPropertyValues } from "src/api/generated/erp/configurator/model/configurationPropertyValues.ts";
import { SxProps, Theme } from "@mui/material/styles";
import {
	NonLayoutTransformedConfigurationComponent
} from "src/components/views/erp/configurator/configuratorForm/components/types/NonLayoutTransformedConfigurationComponent.tsx";

export interface PartConfiguratorFormProps {
	configuratorType: PartConfiguratorType;
	propertyValues: ConfigurationPropertyValues;
	components: TransformedConfigurationComponent[];
	componentsWithDocument: number[];
	onComponentInputChanged: (params: OnComponentInputChangedParams) => Promise<OnComponentInputChangedResult>;
	disabled?: boolean;
	componentIdWithActiveDocument: number | null;
	setComponentIdWithActiveDocument: (componentId: number) => void;
	configuratorComponentSx?: (component: NonLayoutTransformedConfigurationComponent) => SxProps<Theme>;
	configuratorComponentContextMenu?: (component: NonLayoutTransformedConfigurationComponent) => React.ReactNode;
}

export interface OnComponentInputChangedParams {
	component: TransformedConfigurationComponent;
	newValue: ConfiguratorInput_InputValue_Scalar;
}

export interface OnComponentInputChangedResult {
	newConfiguratorInput?: ConfiguratorInput;
}

export const PartConfiguratorForm = (props: PartConfiguratorFormProps) => {
	const {
		configuratorType,
		components,
		onComponentInputChanged,
		disabled = false,
		setComponentIdWithActiveDocument,
		componentIdWithActiveDocument,
		componentsWithDocument,
		configuratorComponentSx,
		configuratorComponentContextMenu,
	} = props;

	const { logErrorAndShowOnDialog } = useErrorDialog();

	const componentApiRefByComponentNames = useRef<Record<string, ConfiguratorComponentApi>>({});

	const nonSubConfiguratorComponents = useMemo(
		() =>
			components.filter(
				(component) => component.type !== "subConfigurator" && component.type !== "subConfiguratorList",
			),
		[components],
	);

	return (
		<PartConfiguratorDocumentContextProvider
			componentsWithDocument={componentsWithDocument}
			componentIdWithActiveDocument={componentIdWithActiveDocument}
			setComponentIdWithActiveDocument={setComponentIdWithActiveDocument}
		>
			<ConfiguratorTabsLayout
				allComponents={nonSubConfiguratorComponents}
				renderChildComponent={(component) => (
					<ConfiguratorComponent
						key={component.configurationComponentId}
						component={component}
						configuratorType={configuratorType}
						onSubmit={async (newValue) => {
							await onSubmitComponent(component, newValue);
						}}
						apiRef={(api) => {
							if (api != null) {
								setComponentApiRef(component, api);
							}
						}}
						disabled={disabled}
						sx={configuratorComponentSx == null ? undefined : configuratorComponentSx(component)}
						contextMenu={
							configuratorComponentContextMenu == null ? undefined : (
								() => configuratorComponentContextMenu(component)
							)
						}
					/>
				)}
			/>
		</PartConfiguratorDocumentContextProvider>
	);

	async function onSubmitComponent(
		component: TransformedConfigurationComponent,
		value: ConfiguratorInput_InputValue_Scalar,
	) {
		try {
			const { newConfiguratorInput } = await onComponentInputChanged({
				component: component,
				newValue: value,
			});
			if (newConfiguratorInput != null) {
				newConfiguratorInput.inputComponentValues.forEach(({ componentName, value }) => {
					const componentApi = componentApiRefByComponentNames.current[componentName];
					componentApi?.setValue(value);
				});
			}
		} catch (e) {
			logErrorAndShowOnDialog(e);
		}
	}

	function setComponentApiRef(component: TransformedConfigurationComponent, api: ConfiguratorComponentApi) {
		componentApiRefByComponentNames.current[component.name] = api;
	}
};

interface ConfiguratorTabsLayoutProps extends Pick<ConfiguratorSectionComponentProps, "renderChildComponent"> {
	allComponents: TransformedConfigurationComponent[];
}

const ConfiguratorTabsLayout = ({ allComponents, renderChildComponent }: ConfiguratorTabsLayoutProps) => {
	const tabsWithChildren = useMemo(
		() => groupConfiguratorComponentsToTabsAndSections(allComponents),
		[allComponents],
	);

	return (
		<VerticalBox sx={{ flex: 1 }}>
			<AavoMosaic
				layout={{
					type: "tabs",
					items: tabsWithChildren.map(({ tab, sections }) => ({
						type: "panel",
						title: tab.label,
						content: (
							<ConfiguratorTabComponent tabComponent={tab}>
								{sections.map((section) => (
									<ConfiguratorSectionComponent
										key={section.sectionBreakComponent?.configurationComponentId ?? -1}
										components={section}
										renderChildComponent={renderChildComponent}
									/>
								))}
							</ConfiguratorTabComponent>
						),
					})),
				}}
			/>
		</VerticalBox>
	);
};
